import { useQuery } from '@apollo/client';

import getCountryForTypeform from 'common/countries/getCountryForTypeform';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import { throwIfErrorPresent } from 'features/Onboarding/utils';

import { ShineStartBusinessInfoDocument } from './graphql/queries/shineStartBusinessInfo.gql';
import isYoungerThan26 from './isYoungerThan26';

const useTypeformDataQuery = () => {
  const userLocale = useCurrentLanguage();
  const { companyProfileId } = useCompanyContext();

  if (companyProfileId === undefined) {
    throw Error('useTypeformDataQuery called without a company');
  }

  const { data, error, loading } = useQuery(ShineStartBusinessInfoDocument, {
    variables: {
      companyProfileId,
    },
  });
  throwIfErrorPresent(error);

  if (loading || data === undefined) {
    return { loading: true };
  }

  const {
    viewer: {
      company: { shineStart },
      profile: {
        birthdate,
        city,
        country,
        firstName,
        gender,
        lastName,
        nationality,
        phone,
        street,
        uid,
        zip,
      },
    },
  } = data;

  /**
   * Typeform only allows lowercase letters for hidden fields naming
   * @see https://www.typeform.com/help/a/using-hidden-fields-360052676612/
   */
  const typeformData = {
    birthdate: birthdate ?? undefined,
    city: city ?? undefined,
    country: getCountryForTypeform(country ?? undefined),
    cpid: companyProfileId,
    firstname: firstName,
    gender: gender ?? undefined,
    language: userLocale,
    lastname: lastName,
    lessthan26years: `${isYoungerThan26(birthdate || '')}`,
    nationality: getCountryForTypeform(nationality ?? undefined),
    phone: phone ?? undefined,
    shineStartId: shineStart?.shineStartId,
    street: street ?? undefined,
    uid: uid,
    zip: zip ?? undefined,
  };

  return { loading: false, typeformData };
};

export default useTypeformDataQuery;
